import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, Loader2 } from 'lucide-react';

export default function PublicRegistration() {
  const { publicLink } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    loadEvaluation();
  }, [publicLink]);

  const loadEvaluation = async () => {
    try {
      const response = await axios.get(`${API}/evaluations/link/${publicLink}`);
      setEvaluation(response.data);
      
    } catch (error) {
      console.error('Error loading evaluation:', error);
      toast.error('Link de avaliação inválido ou expirado');
      setTimeout(() => navigate('/login'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/public/register`, {
        ...formData,
        evaluation_id: evaluation.id
      });

      toast.success('🎉 Cadastro realizado com sucesso!');
      
      // Show loading animation
      setTimeout(() => {
        // Redirect to assessment with the invite token
        navigate(`/avaliacao/${response.data.invite_token}/iniciar`);
      }, 1500);

    } catch (error) {
      console.error('Error registering:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao realizar cadastro';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-blue-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-[#f97316]" />
          <p className="mt-4 text-gray-600">Carregando avaliação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Brain className="w-12 h-12 text-[#f97316]" />
            <h1 className="text-4xl font-bold text-[#f97316]">DISC DRSUPORTI</h1>
          </div>
          <p className="text-xl text-[#2563eb] font-semibold">{evaluation?.name || 'Avaliação Comportamental'}</p>
          {evaluation?.description && (
            <p className="text-gray-600 mt-2">{evaluation.description}</p>
          )}
        </div>

        {/* Main Content - 2 Column Layout */}
        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Left Column - Welcome & Info */}
          <div className="space-y-6">
            <Card className="border-2 border-[#f97316]/20 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-[#f97316]">Bem-vindo! 👋</CardTitle>
                <CardDescription className="text-base">
                  Antes de iniciar seu teste de perfil comportamental, precisamos de algumas informações.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-blue-50 border-l-4 border-[#2563eb] p-4 rounded">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-[#2563eb]">💬 Dica da Cíntia:</span>
                      {' '}Não se preocupe, não há respostas certas ou erradas! Seja autêntico e responda com naturalidade.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-semibold text-gray-800">O que você vai fazer:</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <span className="text-[#f97316] font-bold">1.</span>
                        <span>Preencher suas informações básicas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#f97316] font-bold">2.</span>
                        <span>Responder 20 perguntas sobre seu comportamento</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#f97316] font-bold">3.</span>
                        <span>Receber seu perfil DISC personalizado por email</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-orange-50 p-4 rounded border border-[#f97316]/30">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">⏱️ Tempo estimado:</span> 10-15 minutos
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Registration Form */}
          <Card className="border-2 border-[#2563eb]/20 shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl text-[#2563eb]">Suas Informações</CardTitle>
              <CardDescription>
                Preencha os campos abaixo para começar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-gray-700">
                    Nome Completo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="full_name"
                    type="text"
                    placeholder="João Silva"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    className="border-gray-300 focus:border-[#f97316] focus:ring-[#f97316]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="joao@exemplo.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="border-gray-300 focus:border-[#f97316] focus:ring-[#f97316]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-700">
                    Telefone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(11) 98765-4321"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="border-gray-300 focus:border-[#f97316] focus:ring-[#f97316]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white py-6 text-lg font-semibold"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Cíntia está preparando sua avaliação...
                    </>
                  ) : (
                    <>
                      🧠 Iniciar Minha Avaliação DISC
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Ao continuar, você concorda em receber comunicações sobre sua avaliação
                </p>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-gray-600 text-sm">
          <p>Desenvolvido por <span className="font-semibold text-[#f97316]">Cíntia</span> 💡 - Sua assistente virtual em análise comportamental</p>
          <p className="mt-1">DRSUPORTI - Desenvolvendo Talentos</p>
        </div>
      </div>
    </div>
  );
}
